import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./JudgmentViewer.css";
import { apiFetch } from "../utils/apifetch";
import { useAuth } from "../App";

const buildFileUrl = (path) => {
  if (!path) return null;
  const normalized = path.replace(/\\/g, "/").replace(/^\/+/, "");
  const baseUrl = import.meta.env.VITE_API_URL.replace(/\/api\/?$/, "");
  return `${baseUrl}/${normalized}`;
};

export default function JudgmentViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [judgment, setJudgment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiFetch(`/judgments/${id}`, {}, logout, navigate);
        const data = await res.json();
        setJudgment(data);
      } catch {
        setError("Unable to load judgment");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, logout, navigate]);

  if (loading) return <div className="profile-loading">Loading judgment…</div>;
  if (error) return <div className="profile-error">{error}</div>;
  if (!judgment) return null;

  // ✅ CORRECT FIELD FROM DB

  const fullPath =
    judgment.PathName && judgment.UploadedFilename
      ? `${judgment.PathName}/${judgment.UploadedFilename}`
      : null;

  const pdfUrl = buildFileUrl(fullPath);

  // 🔍 DEBUG
  // console.log("PathName:", judgment.PathName);
  // console.log("UploadedFilename:", judgment.UploadedFilename);
  // console.log("Full Path:", fullPath);
  // console.log("Final PDF URL:", pdfUrl);

  return (
    <div className="judgment-viewer">
      <button className="back-btn" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {/* ===== CITATION ===== */}
      <div className="subtitle-1">Citation:</div>
      <div className="judgment-viewer-description">
        {judgment.Citation}
      </div>

      {/* ===== COURT ===== */}
      <div className="subtitle-1">Court:</div>
      <div className="judgment-viewer-description">
        {judgment.HighCourtName} ({judgment.PlaceOfCourt})
      </div>

      {/* // ===== JUDGES ===== */}
      <div className="subtitle-1">Judges:</div>
      <div className="judgment-viewer-description">
        {judgment.Judges && judgment.Judges.length > 0 ? (
          judgment.Judges.map((j, index) => (
            <div key={index}>
              {j.JudgeTitle} {j.JudgeInitial} {j.JudgeName}
            </div>
          ))
        ) : (
          "-"
        )}
      </div>


      {/* ===== CASE TYPE ===== */}
      <div className="subtitle-1">Case Type:</div>
      <div className="judgment-viewer-description">
        {judgment.CaseType}
      </div>

      {/* ===== DATE ===== */}
      <div className="subtitle-1">Judgment Date:</div>
      <div className="judgment-viewer-description">
        {new Date(judgment.JudgmentDate).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })}
      </div>

      {/* ===== PETITIONERS ===== */}
      <div className="subtitle-1">Petitioners:</div>
      <div className="judgment-viewer-description">
        {judgment.PetitionersDetails}
      </div>

      {/* ===== RESPONDENTS ===== */}
      <div className="subtitle-1">Respondents:</div>
      <div className="judgment-viewer-description">
        {judgment.RespondentsDetails}
      </div>

      {/* ===== HEADNOTES ===== */}
      <div className="subtitle-1">Headnotes:</div>
      <div className="judgment-viewer-description">
        {judgment.Headnotes}
      </div>
      {/* // ===== ACTS & SECTIONS ===== */}
      <div className="subtitle-1">Acts & Sections:</div>
      <div className="judgment-viewer-description">
        {judgment.Acts && judgment.Acts.length > 0 ? (
          judgment.Acts.map((a, index) => (
            <div key={index}>
              <strong>{a.ShortName}</strong>
              {a.SectionNumbers ? ` – Sec: ${a.SectionNumbers}` : ""}
            </div>
          ))
        ) : (
          "-"
        )}
      </div>
      {/* ===== PDF VIEW ===== */}
      <div className="judgment-pdf-container">
        {pdfUrl ? (
          <>
            <div className="pdf-toolbar">📄 Judgment PDF</div>
            <iframe
              src={pdfUrl}
              title="Judgment PDF"
              loading="lazy"
            />
          </>
        ) : (
          <div className="no-pdf">No Judgment PDF uploaded</div>
        )}
      </div>
    </div>
  );
}